import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './AskAIPanel.css';

export default function AskAIPanel() {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 0.6) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
        setIsExpanded(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const promptText = "Request an AI summary of SmushLabs from https://www.smushlabs.ai/?utm_source=ai-chatgpt";
  const encodedPrompt = encodeURIComponent(promptText);

  const links = {
    chatgpt: `https://chatgpt.com/?q=${encodedPrompt}`,
    claude: `https://claude.ai/new?q=${encodedPrompt}`,
    gemini: `https://gemini.google.com/app`,
    grok: `https://x.com/i/grok?text=${encodedPrompt}`,
    perplexity: `https://www.perplexity.ai/?q=${encodedPrompt}`
  };

  const copyPrompt = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(promptText);
    alert("Prompt copied! Paste it into your favorite AI.");
  };

  const aiButtons = (
    <>
      <a href={links.chatgpt} target="_blank" rel="noopener noreferrer" className="ask-ai-btn" title="Ask ChatGPT">
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M22.3 9.8a6 6 0 00-.5-4.9 6 6 0 00-6.5-2.9A6.1 6.1 0 005 4.2a6 6 0 00-4 2.9 6 6 0 00.7 7.1 6 6 0 00.5 4.9 6.1 6.1 0 006.5 2.9A6 6 0 0013.3 24a6.1 6.1 0 005.8-4.2 6 6 0 004-2.9 6.1 6.1 0 00-.7-7.1zm-9 12.6a4.5 4.5 0 01-2.9-1l.1-.1 4.8-2.8a.8.8 0 00.4-.7V11l2 1.2v5.6a4.5 4.5 0 01-4.5 4.5zM3.6 18.3a4.5 4.5 0 01-.5-3l.1.1 4.8 2.8a.8.8 0 00.8 0l5.8-3.4v2.3l-4.8 2.8a4.5 4.5 0 01-6.1-1.6zM2.3 7.9a4.5 4.5 0 012.4-2V11.6a.8.8 0 00.4.7l5.8 3.4-2 1.2-4.8-2.8A4.5 4.5 0 012.3 7.9zm16.6 3.9l-5.8-3.4 2-1.2 4.8 2.8a4.5 4.5 0 01-.7 8.1v-5.7a.8.8 0 00-.4-.7zm2-3l-.1-.1-4.8-2.8a.8.8 0 00-.8 0L9.4 9.2V6.9l4.8-2.8a4.5 4.5 0 016.7 4.7zM8.3 12.9l-2-1.2V6.1a4.5 4.5 0 017.4-3.5l-.1.1-4.8 2.8a.8.8 0 00-.4.7zm1.1-2.4l2.6-1.5 2.6 1.5v3l-2.6 1.5-2.6-1.5z"></path></svg>
      </a>
      <a href={links.claude} target="_blank" rel="noopener noreferrer" className="ask-ai-btn" title="Ask Claude">
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="m5 16 4-3h1-9v-1H0v-1h5l2 1h3-1v-1l-2-1-3-2-1-1H2V4l1 1h1l2 2 3 2V8L8 6 6 4V0h2l1 2 1 2 1 3 1 1v1-2l1-2V1l1-1 1 1v3l-1 3v1l1-1 2-2 1-1V3h2l1 1-1 1-1 1v1l-2 2-1 2h1l3-1h1l2-1 1 1v1h-2l-3 1-3 1h7l1 1v1l-1 1-2-1-4-1h-1l1 1 2 2 2 2v1l-2-1-1-1-2-2v1l2 4 1 1h-2l-1-2-2-2-1-2-1 7v1h-1l-1-1 1-2v-3l1-2v-1l-2 2-2 3-2 2H5v-1h1l2-3 2-2 1-1v-1h-1l-6 5H3v-2z"></path></svg>
      </a>
      <a href={links.gemini} target="_blank" rel="noopener noreferrer" className="ask-ai-btn" title="Ask Gemini">
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11 19.3Q12 21.5 12 24q0-2.5.9-4.7 1-2.2 2.6-3.8t3.8-2.6Q21.5 12 24 12q-2.5 0-4.7-.9a12.3 12.3 0 01-3.8-2.6 12.3 12.3 0 01-2.6-3.8Q12 2.5 12 0q0 2.5-1 4.7-.9 2.2-2.5 3.8a12.3 12.3 0 01-3.8 2.6Q2.5 12 0 12q2.5 0 4.7 1 2.2.9 3.8 2.5t2.5 3.8"></path></svg>
      </a>
      <a href={links.grok} target="_blank" rel="noopener noreferrer" className="ask-ai-btn" title="Ask Grok">
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M14.2 10.2L23 0h-2.1l-7.6 8.8L7.3 0H.3l9.2 13.3L.3 24h2.1l8-9.3L16.7 24H23.7zm-2.8 3.3l-.9-1.3L3.1 1.6h3.2l6 8.5.9 1.3 7.8 11.1h-3.2z"></path></svg>
      </a>
      <a href={links.perplexity} target="_blank" rel="noopener noreferrer" className="ask-ai-btn" title="Ask Perplexity">
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M22.4 7.1h-2.3V.1l-7.5 6.4V.2h-1.2v6.2L4.5 0v7.1H1.6v10.4h2.9V24l6.9-6.4v6.2h1.2v-6l6.9 6.2v-6.5h2.9V7.1zm-3.5-4.5v4.5h-5.4l5.4-4.5zm-13.3.1l4.9 4.5H5.6V2.6zM2.8 16.3V8.2h7.8l-6.1 6.1v2zm2.9 5v-6.5l5.8-5.8v7l-5.8 5.3zm12.7 0l-5.8-5.2V9.1l5.8 5.8v6.6zm2.9-5.1h-1.7v-2L13.4 8.2h7.8v8.1z"></path></svg>
      </a>
      <button onClick={copyPrompt} className="ask-ai-btn" title="Copy Prompt">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"></rect><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path></svg>
      </button>
    </>
  );

  return ReactDOM.createPortal(
    <>
      {/* Desktop: Original horizontal panel */}
      <div className={`ask-ai-panel ask-ai-desktop ${isVisible ? 'is-visible' : ''}`}>
        <span className="ask-ai-label">Ask your favorite AI about how SmushChat helps your visitors:</span>
        <div className="ask-ai-buttons">
          {aiButtons}
        </div>
      </div>

      {/* Mobile: FAB with expandable drawer */}
      <div className={`ask-ai-fab-container ask-ai-mobile ${isVisible ? 'is-visible' : ''} ${isExpanded ? 'is-expanded' : ''}`}>
        
        {showTooltip && !isExpanded && (
          <div className="ask-ai-tooltip">
            <span>Ask your favorite AI about how SmushChat helps your visitors</span>
            <button 
              onClick={(e) => { e.stopPropagation(); setShowTooltip(false); }} 
              className="ask-ai-tooltip-close"
              aria-label="Dismiss"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        )}

        <div className="ask-ai-buttons ask-ai-fab-buttons">
          {aiButtons}
        </div>
        
        <button 
          className="ask-ai-main-fab" 
          onClick={() => {
            setIsExpanded(!isExpanded);
            setShowTooltip(false);
          }}
          title="Ask AI about SmushChat"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '24px', height: '24px' }}>
            <path d="m12 3-1.9 5.8a2 2 0 0 1-1.2 1.2L3 12l5.8 1.9a2 2 0 0 1 1.2 1.2L12 21l1.9-5.8a2 2 0 0 1 1.2-1.2L21 12l-5.8-1.9a2 2 0 0 1-1.2-1.2L12 3Z"/>
          </svg>
        </button>
      </div>
    </>,
    document.body
  );
}
